import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Link as LinkIcon, X, MessageCircle, Mail, Send, Eye, Upload, UserPlus, Trash2, Users, CheckCircle2 } from 'lucide-react';
import { API } from '../../App';
import { convertGoogleDriveLink } from '../../utils/imageUtils';

const AdminSettingsPage = ({ adminToken }) => {
  const [settings, setSettings] = useState({
    hero_images: [{ desktop: '', mobile: '' }],
    site_title: '',
    site_description: '',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    whatsapp_enabled: true,
    whatsapp_number: '917980063435'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mailerSending, setMailerSending] = useState(false);
  const [mailerLogs, setMailerLogs] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [newsletterStats, setNewsletterStats] = useState({ total: 0, active: 0, converted: 0, unsubscribed: 0, bounced: 0 });
  const [newsletterSubs, setNewsletterSubs] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchMailerLogs();
    fetchNewsletterSubs();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      const data = response.data;
      
      // Normalize hero slides to {desktop, mobile} shape.
      // Supports legacy string entries and legacy single hero_image field.
      const normalizeSlide = (item) => {
        if (typeof item === 'string') return { desktop: item, mobile: '' };
        return { desktop: item?.desktop || '', mobile: item?.mobile || '' };
      };
      if (data.hero_images && Array.isArray(data.hero_images) && data.hero_images.length > 0) {
        setSettings({ ...data, hero_images: data.hero_images.map(normalizeSlide) });
      } else if (data.hero_image) {
        setSettings({ ...data, hero_images: [{ desktop: data.hero_image, mobile: '' }] });
      } else {
        setSettings({ ...data, hero_images: [{ desktop: '', mobile: '' }] });
      }
    } catch (error) {
      console.error('Failed to fetch settings', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchMailerLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/mailer/logs`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setMailerLogs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch mailer logs', error);
    }
  };

  const fetchNewsletterSubs = async () => {
    try {
      const response = await axios.get(`${API}/admin/newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setNewsletterSubs(response.data?.subscribers || []);
      setNewsletterStats(response.data?.stats || { total: 0, active: 0, converted: 0, unsubscribed: 0, bounced: 0 });
    } catch (error) {
      console.error('Failed to fetch newsletter subscribers', error);
    }
  };

  const handleNewsletterImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a .csv file. In Excel: File > Save As > CSV.');
      e.target.value = '';
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/newsletter/import`, fd, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.imported} new subscriber(s)`);
      fetchNewsletterSubs();
    } catch (error) {
      console.error('Import failed', error);
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleDeleteSubscriber = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      await axios.delete(`${API}/admin/newsletter/subscribers/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Subscriber removed');
      fetchNewsletterSubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleBulkRemove = async () => {
    // Parse pasted text — split by newlines, commas, semicolons, spaces
    const emails = bulkEmails
      .split(/[\s,;\n]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0);
    if (emails.length === 0) {
      toast.error('Paste at least one email address');
      return;
    }
    if (!window.confirm(`Remove ${emails.length} email(s) from all mailing lists?`)) return;
    setBulkRemoving(true);
    setBulkResult(null);
    try {
      const res = await axios.post(`${API}/admin/newsletter/bulk-remove`, { emails }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setBulkResult(res.data);
      toast.success(res.data.message || 'Bulk removal complete');
      setBulkEmails('');
      fetchNewsletterSubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk removal failed');
    } finally {
      setBulkRemoving(false);
    }
  };

  const handlePreviewMailer = async () => {
    setPreviewLoading(true);
    try {
      const response = await axios.get(`${API}/admin/mailer/preview`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setPreviewHtml(response.data.html || '');
      setShowPreview(true);
      if (!response.data.sections) {
        toast.warning('No visible products available — mailer will be empty.');
      }
    } catch (error) {
      console.error('Preview failed', error);
      toast.error(error.response?.data?.detail || 'Failed to load mailer preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendMailerNow = async () => {
    if (!window.confirm('Send the weekly mailer to all approved & subscribed customers right now?')) return;
    setMailerSending(true);
    try {
      const response = await axios.post(`${API}/admin/mailer/send-weekly`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const { sent = 0, failed = 0, skipped = 0, removed_users = 0, removed_subscribers = 0 } = response.data || {};
      const removed = removed_users + removed_subscribers;
      const removedNote = removed ? ` · Auto-removed ${removed} bad address${removed !== 1 ? 'es' : ''}` : '';
      toast.success(`Mailer dispatched. Sent: ${sent}, Failed: ${failed}, Skipped: ${skipped}${removedNote}`);
      fetchMailerLogs();
    } catch (error) {
      console.error('Mailer send failed', error);
      toast.error(error.response?.data?.detail || 'Failed to send weekly mailer');
    } finally {
      setMailerSending(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Store original URLs as-is (conversion happens on display).
      // Persist slides as {desktop, mobile} objects; drop slides with no desktop image.
      const cleanedSlides = settings.hero_images
        .map((s) => ({
          desktop: (s?.desktop || '').trim(),
          mobile: (s?.mobile || '').trim(),
        }))
        .filter((s) => s.desktop);

      const updatedSettings = {
        ...settings,
        hero_images: cleanedSlides,
      };
      
      await axios.put(`${API}/admin/settings`, updatedSettings, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      setSettings({
        ...updatedSettings,
        hero_images: cleanedSlides.length > 0 ? cleanedSlides : [{ desktop: '', mobile: '' }],
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addHeroImage = () => {
    setSettings({
      ...settings,
      hero_images: [...settings.hero_images, { desktop: '', mobile: '' }]
    });
  };

  const removeHeroImage = (index) => {
    const newImages = settings.hero_images.filter((_, i) => i !== index);
    setSettings({
      ...settings,
      hero_images: newImages.length > 0 ? newImages : [{ desktop: '', mobile: '' }]
    });
  };

  const updateHeroImage = (index, field, value) => {
    const newImages = settings.hero_images.map((slide, i) =>
      i === index ? { ...slide, [field]: value } : slide
    );
    setSettings({
      ...settings,
      hero_images: newImages
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="py-8" data-testid="admin-settings-page">
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold mb-2">Site Settings</h1>
        <p className="text-muted-foreground">Manage your website settings and appearance</p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        {/* Homepage Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-border p-6"
        >
          <h2 className="text-2xl font-heading font-semibold mb-6">Homepage Settings</h2>
          
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold uppercase tracking-wide">
                  Hero Images (Carousel) *
                </label>
                <button
                  type="button"
                  onClick={addHeroImage}
                  className="bg-luxury-gold text-white px-4 py-2 text-sm hover:bg-luxury-darkGold transition-colors"
                >
                  + Add Image
                </button>
              </div>
              
              {settings.hero_images.map((slide, index) => (
                <div key={index} className="mb-4 p-4 border border-border bg-accent/30">
                  <div className="flex items-start space-x-3">
                    <div className="flex-1 space-y-5">
                      {/* Slide header */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Slide {index + 1}
                        </span>
                      </div>

                      {/* Desktop image field */}
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5">
                          Desktop Image (landscape) *
                        </label>
                        <input
                          type="text"
                          value={slide.desktop || ''}
                          onChange={(e) => updateHeroImage(index, 'desktop', e.target.value)}
                          className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-white py-3 px-4 outline-none transition-colors"
                          placeholder="https://example.com/desktop.jpg or Google Drive link"
                          data-testid={`hero-image-desktop-input-${index}`}
                        />
                        {slide.desktop && (
                          <div className="mt-3">
                            <div className="aspect-video w-full max-w-md overflow-hidden border border-border">
                              <img
                                src={convertGoogleDriveLink(slide.desktop)}
                                alt={`Desktop preview ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/1920x1080?text=Invalid+Image+URL';
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Mobile image field (optional) */}
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5">
                          Mobile Image (portrait) <span className="text-muted-foreground font-normal normal-case tracking-normal">— optional, falls back to desktop</span>
                        </label>
                        <input
                          type="text"
                          value={slide.mobile || ''}
                          onChange={(e) => updateHeroImage(index, 'mobile', e.target.value)}
                          className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-white py-3 px-4 outline-none transition-colors"
                          placeholder="https://example.com/mobile.jpg or Google Drive link (leave blank to reuse desktop)"
                          data-testid={`hero-image-mobile-input-${index}`}
                        />
                        {slide.mobile && (
                          <div className="mt-3">
                            <div className="w-40 aspect-[9/16] overflow-hidden border border-border">
                              <img
                                src={convertGoogleDriveLink(slide.mobile)}
                                alt={`Mobile preview ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/720x1280?text=Invalid+Image+URL';
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {settings.hero_images.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeHeroImage(index)}
                        className="text-destructive hover:text-destructive/80 p-2"
                        data-testid={`hero-image-remove-${index}`}
                      >
                        <X size={20} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200">
                <div className="flex items-start space-x-2">
                  <LinkIcon size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-800">
                    <p className="font-semibold mb-1">✓ Separate desktop &amp; mobile images per slide</p>
                    <p>Mobile image is shown on screens below 768px; leave blank to reuse the desktop image. Google Drive links supported.</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Recommended size: 1920x1080px or larger. Images will auto-slide every 5 seconds.
              </p>
            </div>
          </div>
        </motion.div>

        {/* General Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-border p-6"
        >
          <h2 className="text-2xl font-heading font-semibold mb-6">General Settings</h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold uppercase tracking-wide mb-2">
                Site Title *
              </label>
              <input
                type="text"
                value={settings.site_title}
                onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
                className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                placeholder="Ambika Fashion"
                data-testid="site-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold uppercase tracking-wide mb-2">
                Site Description *
              </label>
              <textarea
                value={settings.site_description}
                onChange={(e) => setSettings({ ...settings, site_description: e.target.value })}
                rows="3"
                className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                placeholder="Premium B2B wholesale platform for traditional Indian ethnic wear"
                data-testid="site-description-input"
              />
            </div>
          </div>
        </motion.div>

        {/* Contact Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-border p-6"
        >
          <h2 className="text-2xl font-heading font-semibold mb-6">Contact Information</h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold uppercase tracking-wide mb-2">
                Contact Email *
              </label>
              <input
                type="email"
                value={settings.contact_email}
                onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                placeholder="info@ambikafashion.com"
                data-testid="contact-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold uppercase tracking-wide mb-2">
                Contact Phone *
              </label>
              <input
                type="tel"
                value={settings.contact_phone}
                onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                placeholder="+91 123 456 7890"
                data-testid="contact-phone-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold uppercase tracking-wide mb-2">
                Contact Address *
              </label>
              <textarea
                value={settings.contact_address}
                onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })}
                rows="3"
                className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                placeholder="123 Fashion Street, Mumbai, India"
                data-testid="contact-address-input"
              />
            </div>
          </div>
        </motion.div>

        {/* WhatsApp Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-border p-6"
        >
          <h2 className="text-2xl font-heading font-semibold mb-6 flex items-center gap-3">
            <MessageCircle className="text-[#25D366]" size={28} />
            WhatsApp Button Settings
          </h2>
          
          <div className="space-y-6">
            {/* WhatsApp Toggle */}
            <div className="flex items-center justify-between p-4 bg-accent/50 border border-border rounded-lg">
              <div>
                <label className="block text-sm font-semibold mb-1">
                  Show WhatsApp Button
                </label>
                <p className="text-sm text-muted-foreground">
                  Display floating WhatsApp icon on website for customer inquiries
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSettings({ ...settings, whatsapp_enabled: !settings.whatsapp_enabled })}
                className={`relative w-14 h-8 rounded-full transition-colors duration-300 ${
                  settings.whatsapp_enabled ? 'bg-[#25D366]' : 'bg-gray-300'
                }`}
                data-testid="whatsapp-toggle"
              >
                <span
                  className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                    settings.whatsapp_enabled ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* WhatsApp Number */}
            <div className={settings.whatsapp_enabled ? '' : 'opacity-50 pointer-events-none'}>
              <label className="block text-sm font-semibold uppercase tracking-wide mb-2">
                WhatsApp Number (with country code)
              </label>
              <div className="flex items-center gap-2">
                <span className="text-lg">+</span>
                <input
                  type="text"
                  value={settings.whatsapp_number || ''}
                  onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value.replace(/\D/g, '') })}
                  className="flex-1 border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                  placeholder="917980063435"
                  data-testid="whatsapp-number-input"
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Enter without + sign. Example: 917980063435 (91 = India country code)
              </p>
            </div>

            {/* Preview */}
            {settings.whatsapp_enabled && settings.whatsapp_number && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium mb-2">Preview Link:</p>
                <a 
                  href={`https://wa.me/${settings.whatsapp_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-green-700 hover:underline break-all"
                >
                  https://wa.me/{settings.whatsapp_number}
                </a>
              </div>
            )}
          </div>
        </motion.div>

        {/* Weekly Mailer Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white border border-border p-6"
          data-testid="weekly-mailer-card"
        >
          <h2 className="text-2xl font-heading font-bold mb-1 flex items-center space-x-2">
            <Mail size={22} className="text-primary" />
            <span>Weekly Collections Mailer</span>
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Automatically dispatched every <strong>Monday at 9:00 AM IST</strong> to all approved customers who haven&apos;t unsubscribed. Curates 1–2 featured products from each visible category with an &quot;Order Now&quot; CTA.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <button
              type="button"
              onClick={handlePreviewMailer}
              disabled={previewLoading}
              className="inline-flex items-center justify-center space-x-2 border border-primary text-primary hover:bg-primary hover:text-primary-foreground px-5 py-3 font-medium transition-colors disabled:opacity-50"
              data-testid="mailer-preview-btn"
            >
              <Eye size={18} />
              <span>{previewLoading ? 'Loading…' : 'Preview Mailer'}</span>
            </button>
            <button
              type="button"
              onClick={handleSendMailerNow}
              disabled={mailerSending}
              className="inline-flex items-center justify-center space-x-2 bg-primary text-primary-foreground hover:bg-primary/90 px-5 py-3 font-medium transition-colors disabled:opacity-50"
              data-testid="mailer-send-now-btn"
            >
              <Send size={18} />
              <span>{mailerSending ? 'Sending…' : 'Send Mailer Now'}</span>
            </button>
          </div>

          {showPreview && previewHtml && (
            <div className="mb-6" data-testid="mailer-preview-frame-wrap">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Preview (live HTML)</p>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center space-x-1"
                  data-testid="mailer-preview-close"
                >
                  <X size={14} />
                  <span>Close</span>
                </button>
              </div>
              <iframe
                title="Mailer Preview"
                srcDoc={`<!doctype html><html><head><base target="_blank"><meta charset="utf-8"></head><body style="margin:0;">${previewHtml}</body></html>`}
                className="w-full bg-white border border-border"
                style={{ height: 700 }}
                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                data-testid="mailer-preview-iframe"
              />
              <p className="text-xs text-muted-foreground mt-2">All links in the actual email open in a new tab. Inside this preview, link clicks open in a new tab too.</p>
            </div>
          )}

          <div data-testid="mailer-logs-section">
            <h3 className="text-sm font-semibold mb-2 uppercase tracking-wide text-muted-foreground">Recent Sends</h3>
            {mailerLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No mailer dispatches yet.</p>
            ) : (
              <div className="border border-border divide-y divide-border">
                {mailerLogs.slice(0, 5).map((log) => (
                  <div key={log.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm" data-testid="mailer-log-row">
                    <div>
                      <span className="font-medium">{new Date(log.ran_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>
                      <span className="text-muted-foreground ml-2">· {log.triggered_by}</span>
                    </div>
                    <div className="text-muted-foreground mt-1 sm:mt-0">
                      <span className="text-green-700 font-medium">Sent {log.sent || 0}</span>
                      {log.failed ? <span className="ml-3 text-red-600">Failed {log.failed}</span> : null}
                      {log.skipped ? <span className="ml-3">Skipped {log.skipped}</span> : null}
                      {((log.removed_users || 0) + (log.removed_subscribers || 0)) > 0 ? (
                        <span className="ml-3 text-amber-700">Auto-removed {(log.removed_users || 0) + (log.removed_subscribers || 0)}</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Newsletter Subscribers Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-white border border-border p-6"
          data-testid="newsletter-subscribers-card"
        >
          <h2 className="text-2xl font-heading font-bold mb-1 flex items-center space-x-2">
            <UserPlus size={22} className="text-primary" />
            <span>Newsletter Subscribers</span>
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Upload a CSV of prospective customer emails. They&apos;ll receive the same weekly mailer. When any of them register on the site, we auto-track the conversion below.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="newsletter-stats">
            <div className="p-4 border border-border bg-accent/30">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Total Imported</p>
              <p className="text-2xl font-heading font-bold">{newsletterStats.total}</p>
            </div>
            <div className="p-4 border border-border bg-accent/30">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><Mail size={12} /> Active</p>
              <p className="text-2xl font-heading font-bold text-green-700">{newsletterStats.active}</p>
            </div>
            <div className="p-4 border border-border bg-accent/30">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><CheckCircle2 size={12} /> Converted</p>
              <p className="text-2xl font-heading font-bold text-primary">{newsletterStats.converted}</p>
            </div>
            <div className="p-4 border border-border bg-accent/30">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Unsubscribed</p>
              <p className="text-2xl font-heading font-bold text-muted-foreground">{newsletterStats.unsubscribed}</p>
            </div>
            <div className="p-4 border border-border bg-accent/30" title="Auto-removed after permanent delivery failure">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">⚠ Bounced</p>
              <p className="text-2xl font-heading font-bold text-red-600">{newsletterStats.bounced || 0}</p>
            </div>
          </div>

          {/* Upload */}
          <div className="mb-6 p-5 border border-dashed border-primary/40 bg-accent/30" data-testid="newsletter-upload-area">
            <p className="text-sm font-medium mb-1">Import subscribers from CSV</p>
            <p className="text-xs text-muted-foreground mb-3">
              CSV columns: <code className="px-1 bg-muted text-foreground">email</code>, <code className="px-1 bg-muted text-foreground">name</code>, <code className="px-1 bg-muted text-foreground">business_name</code>. Duplicates are skipped automatically.
              {' '}<a href="data:text/csv;charset=utf-8,email%2Cname%2Cbusiness_name%0Acustomer1%40example.com%2CJane%20Doe%2CSample%20Boutique" download="ambika_newsletter_template.csv" className="text-primary hover:underline">Download template</a>
            </p>
            <div className="flex items-center gap-3">
              <label className={`inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer font-medium transition-colors ${importing ? 'opacity-60 cursor-not-allowed' : ''}`} data-testid="newsletter-upload-btn">
                <Upload size={18} />
                <span>{importing ? 'Importing…' : 'Upload CSV'}</span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleNewsletterImport}
                  disabled={importing}
                  className="hidden"
                  data-testid="newsletter-upload-input"
                />
              </label>
              {importResult && (
                <div className="text-sm text-muted-foreground" data-testid="newsletter-import-result">
                  ✅ {importResult.imported} imported
                  {importResult.skipped_duplicates ? ` · ${importResult.skipped_duplicates} skipped (duplicates)` : ''}
                  {importResult.skipped_invalid ? ` · ${importResult.skipped_invalid} skipped (invalid)` : ''}
                </div>
              )}
            </div>
          </div>

          {/* Bulk Remove */}
          <div className="mb-6 p-5 border border-dashed border-red-400/50 bg-red-50/40" data-testid="bulk-remove-area">
            <p className="text-sm font-medium mb-1 flex items-center gap-2">
              <Trash2 size={14} className="text-red-600" /> Bulk Remove Failed/Bounced Emails
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              Paste emails (one per line, or comma-separated). Matching newsletter subscribers are deleted; matching registered users are quietly unsubscribed (accounts kept).
            </p>
            <textarea
              value={bulkEmails}
              onChange={(e) => setBulkEmails(e.target.value)}
              rows={6}
              placeholder={"aditya221077@gmail.com\naartees@aaraclothings.com\naggarwal_fashion@yahoo.in"}
              className="w-full border border-border focus:border-red-600 focus:ring-1 focus:ring-red-600 px-3 py-2 outline-none font-mono text-sm mb-3"
              data-testid="bulk-remove-textarea"
              disabled={bulkRemoving}
            />
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleBulkRemove}
                disabled={bulkRemoving || !bulkEmails.trim()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="bulk-remove-btn"
              >
                <Trash2 size={16} />
                <span>{bulkRemoving ? 'Removing…' : 'Remove Selected Emails'}</span>
              </button>
              {bulkResult && (
                <div className="text-sm text-muted-foreground" data-testid="bulk-remove-result">
                  ✅ Deleted {bulkResult.deleted_subscribers} · Unsubscribed {bulkResult.unsubscribed_users}
                  {bulkResult.invalid_emails ? ` · ${bulkResult.invalid_emails} invalid` : ''}
                </div>
              )}
            </div>
          </div>

          {/* List */}
          <div data-testid="newsletter-list">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <Users size={14} /> Subscribers
              </h3>
            </div>
            {newsletterSubs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No newsletter subscribers yet. Upload a CSV to get started.</p>
            ) : (
              <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-accent/40 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Email</th>
                      <th className="px-3 py-2 font-medium">Name</th>
                      <th className="px-3 py-2 font-medium">Business</th>
                      <th className="px-3 py-2 font-medium">Imported</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {newsletterSubs.map((s) => (
                      <tr key={s.id} data-testid={`newsletter-sub-row-${s.id}`}>
                        <td className="px-3 py-2 break-all">{s.email}</td>
                        <td className="px-3 py-2 text-muted-foreground">{s.name || '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground">{s.business_name || '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                          {s.imported_at ? new Date(s.imported_at).toLocaleDateString('en-IN') : '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {s.converted_user_id ? (
                            <span className="inline-flex items-center gap-1 text-primary font-medium">
                              <CheckCircle2 size={14} /> Converted
                            </span>
                          ) : s.bounced_at ? (
                            <span className="inline-flex items-center gap-1 text-red-600" title={s.bounce_reason || 'Delivery failed permanently'}>
                              ⚠ Bounced
                            </span>
                          ) : s.subscribed ? (
                            <span className="inline-flex items-center gap-1 text-green-700">
                              <Mail size={14} /> Active
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Unsubscribed</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleDeleteSubscriber(s.id)}
                            className="text-red-600 hover:bg-red-50 p-1.5 border border-transparent hover:border-red-200"
                            title="Remove subscriber"
                            data-testid={`newsletter-delete-${s.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            data-testid="save-settings-button"
          >
            <Save size={20} />
            <span>{saving ? 'Saving...' : 'Save Settings'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettingsPage;
